/**
 * Yield once to the browser so React can commit pending DOM work (overlay
 * media elements) before a caller continues.
 *
 * WHY THIS IS NOT A PLAIN DOUBLE-requestAnimationFrame: rAF is driven by the
 * display refresh, so it is PAUSED whenever the WKWebView is hidden or
 * suspended — exactly the state during an iOS lock-screen CallKit answer. A
 * bare `await` on a double-rAF therefore never resolves while the phone is
 * locked, which stranded the accept path before `acceptCall` ran: the session
 * stayed `phase: 'incoming'`, and the callee's own native-ack ACCEPT echo was
 * then mistaken for a sibling-device answer that disposed the just-joined
 * native media session (web→iOS locked "stuck on Connecting…").
 *
 * So: when the document is hidden, skip rAF entirely and yield on a timer
 * (timers keep running during CallKit/VoIP background execution). When it is
 * visible, keep the double-rAF for correct paint timing but race it against a
 * short timer so a throttled/background-tab frame loop can never hang the
 * caller.
 */

/** Timer fallback for a visible-but-throttled frame loop. */
export const YIELD_TO_DOM_FALLBACK_MS = 50

/**
 * True when the document is in a state where rAF cannot be trusted to fire
 * (hidden tab, backgrounded/locked WebView). Exported for callers that want to
 * skip the yield altogether on their critical path.
 */
export function isDocumentHidden(): boolean {
  if (typeof document === 'undefined') return true
  return document.visibilityState === 'hidden'
}

export const WAIT_VISIBLE_POLL_MS = 250

/**
 * Resolve once the WebView is visible (or the join was aborted). Used to defer
 * JS Chime getUserMedia until iOS has actually foregrounded the app — WebKit
 * mutes capture in a backgrounded WKWebView, and a join started there hangs
 * until the connecting-stall watchdog INTERRUPTs the call (Jaiden 2026-08-18
 * video, +30.7s).
 *
 * Polls rather than depending on `visibilitychange` so a CallKit answer that
 * never delivers the event (suspended WebContent) still unblocks as soon as
 * `document.visibilityState` flips, and so tests don't need a full EventTarget.
 */
export function waitUntilDocumentVisible(
  isAborted: () => boolean,
  pollMs = WAIT_VISIBLE_POLL_MS,
): Promise<'visible' | 'aborted' | 'already-visible'> {
  if (typeof document === 'undefined') return Promise.resolve('already-visible')
  if (!isDocumentHidden()) return Promise.resolve('already-visible')
  return new Promise((resolve) => {
    const tick = () => {
      if (isAborted()) {
        resolve('aborted')
        return
      }
      if (!isDocumentHidden()) {
        resolve('visible')
        return
      }
      setTimeout(tick, pollMs)
    }
    tick()
  })
}

export function yieldToDom(): Promise<void> {
  if (typeof requestAnimationFrame !== 'function' || isDocumentHidden()) {
    return new Promise((resolve) => {
      setTimeout(resolve, 0)
    })
  }
  return new Promise((resolve) => {
    let settled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    const done = () => {
      if (settled) return
      settled = true
      if (timer !== null) clearTimeout(timer)
      resolve()
    }
    timer = setTimeout(done, YIELD_TO_DOM_FALLBACK_MS)
    requestAnimationFrame(() => requestAnimationFrame(done))
  })
}
